import { useState, useEffect, useRef, useCallback } from 'react';
import { normalizeApiResponse } from '../utils/normalizeResp';
import { parsePaginationFromHeaders } from '../utils/pagination';

export function useServerPagination(fetchFn, {
  initialPage = 1,
  initialPerPage = 20,
  autoLoad = true
} = {}) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [perPage, setPerPage] = useState(initialPerPage);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const inFlightRef = useRef(false);

  const load = useCallback(async (customPage = page, customPerPage = perPage, extraParams = {}) => {
    if (inFlightRef.current) return;

    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      inFlightRef.current = false;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    inFlightRef.current = true;

    setLoading(true);
    setError(null);

    try {
      const raw = await fetchFn({
        page: customPage, 
        per_page: customPerPage, 
        signal: controller.signal,
        ...extraParams
      });
      const { data, headers } = normalizeApiResponse(raw, customPerPage);
      const { total, totalPages, page: returnedPage } = parsePaginationFromHeaders(headers, customPerPage);

      if (controller.signal.aborted) return;

      setItems(Array.isArray(data) ? data : []);
      setTotal(total);
      setTotalPages(totalPages || 1);
      if (returnedPage && returnedPage !== customPage) setPage(returnedPage);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.message === 'canceled') return;
      setError(err);
      setItems([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  }, [fetchFn, page, perPage]);

  useEffect(() => {
    if (autoLoad) {
      load(page, perPage);
    }

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
        inFlightRef.current = false;
      }
    };
  }, [page, perPage, load, autoLoad]);

  return {
    items,
    page,
    perPage,
    total,
    totalPages,
    loading,
    error,
    setPage,
    setPerPage,
    refresh: (extraParams = {}) => load(page, perPage, extraParams)
  };
}
